import React from "react";
import * as Yup from "yup";
import addDays from "date-fns/addDays";
import { DateRangePicker } from "rsuite";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box, IconButton } from "@material-ui/core";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
// component
import { formatDate } from "helper/dateFormat";
import { getDateFormat, getDateFormatForDatePicket } from "helper/dateFormat";

// form initial values
export const initialValues = {
  memberName: { id: "", name: "" },
  eventType: { id: "", name: "" },
  isDescriptionConfidencial: false,
  isEventConfidencial: false,
  description: "",
  eventDate: "",
  account: {},
};

const { beforeToday, allowedRange } = DateRangePicker;

const today = new Date();
const y = today.getFullYear();
const m = today.getMonth();

const predefinedRanges = [
  {
    label: "Today",
    value: [new Date(), new Date()],
  },
  {
    label: "Tomorrow",
    value: [addDays(new Date(), 1), addDays(new Date(), 1)],
  },
  {
    label: "Next 7 days",
    value: [new Date(), addDays(new Date(), 7)],
  },
  {
    label: "Next 30 days",
    value: [new Date(), addDays(new Date(), 30)],
  },
  {
    label: "This month",
    value: [new Date(y, m, 1), new Date(y, m + 1, 0)],
  },
  {
    label: "Next month",
    value: [new Date(y, m + 1, 1), new Date(y, m + 2, 0)],
  },
];

// table head and body
export const columns = (
  members,
  eventTypes,
  date,
  setDateRange,
  setDate,
  onEventChange,
  setSelectedFilter
) => [
  {
    field: "fullName",
    headerName: "NAME",
    filterKey: "name",
    hasFilter: true,
    options: members ?? [],
    renderCell: (row) => row?.fullName,
  },
  {
    field: "name",
    headerName: "EVENT TYPE",
    filterKey: "eventType",
    hasFilter: true,
    options: eventTypes ?? [],
    renderCell: (row) => row?.name,
  },
  {
    field: "Date",
    headerName: "DATE",
    filterKey: "dateType",
    hasFilter: true,
    isNotAllNeed: true,
    optionComponent: () => {
      const dateFormate = getDateFormatForDatePicket();
      const placeholderFormat = getDateFormat();

      function convertUTCRangeToLocal(range) {
        if (!range || range.length !== 2) return [null, null];

        const [start, end] = range;

        const toLocal = (date) => {
          if (!date) return null;
          const d = new Date(date);
          return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
        };

        return [toLocal(start), toLocal(end)];
      }

      function convertLocalRangeToUTC(range) {
        if (!range || range.length !== 2) return [null, null];

        const [start, end] = range;

        const toUTC = (date) => {
          if (!date) return null;
          const d = new Date(date);
          return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
        };

        return [toUTC(start), toUTC(end)];
      }

      return (
        <Box sx={{ display: "flex", py: 2, width: "96%" }}>
          <DateRangePicker
            format={dateFormate}
            placeholder={`${placeholderFormat} - ${placeholderFormat}`}
            cleanable
            style={{
              display: "inline-block",
              padding: "0",
              border: "0",
              width: "100%",
              color: "black",
            }}
            size="lg"
            value={
              date?.length === 2 ? convertUTCRangeToLocal(date) : [null, null]
            }
            onKeyPress={(event) => {
              if (!/[0-9]/.test(event.key)) {
                event.preventDefault();
              }
            }}
            disabledDate={allowedRange(
              addDays(new Date(), -364),
              addDays(new Date(), 364)
            )}
            ranges={predefinedRanges}
            onChange={(update) => {
              const utcRange = convertLocalRangeToUTC(update);
              setDateRange(utcRange);
              setDate(utcRange);
              onEventChange("dateType", utcRange);
            }}
          />
          {date?.length > 0 && (
            <>
              <IconButton
                onClick={() => {
                  setDateRange([null, null]);
                  setDate([]);
                  setSelectedFilter((prev) => {
                    const selectedFilters = { ...prev };
                    selectedFilters.dateType = [];
                    return selectedFilters;
                  });
                }}
              >
                <svg
                  height="20"
                  width="20"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                  focusable="false"
                  class="css-tj5bde-Svg"
                >
                  <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z"></path>
                </svg>
              </IconButton>
            </>
          )}
        </Box>
      );
    },
    renderCell: (row) =>
      formatDate({ date: row.DATE, formatType: "defaultWithUTC" }),
  },
  {
    field: "archived",
    headerName: "ARCHIVED",
    filterKey: "archivedType",
    hasFilter: true,
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" },
    ],
    renderCell: (row) =>
      row.archived === 1 ? (
        <CheckCircleIcon color="primary" />
      ) : (
        <CancelIcon color="primary" />
      ),
  },
];

export const selectControlStyles = {
  backgroundColor: "#F3F6F9",
  borderColor: "#F3F6F9",
  color: "#3F4254",
  transition: "all 0.15s ease",
  paddingTop: "6px",
  paddingBottom: "6px",
  cursor: "pointer",
};
// add validation schema
export const validationSchema = Yup.object().shape({
  memberName: Yup.object().shape({
    id: Yup.string().required("Member name is required"),
    name: Yup.string(),
  }),
  eventType: Yup.object().shape({
    id: Yup.string().required("Event type is required"),
    name: Yup.string(),
  }),
  eventDate: Yup.date().required("Event date is required"),
});
// error message

export const renderFormikError = (formik, name) => {
  const getValue = (path, obj) => {
    return path.split(".").reduce((acc, key) => acc?.[key], obj);
  };

  const touched = getValue(name, formik.touched);
  const error = getValue(name, formik.errors);

  return touched && error ? (
    <Box className="text-danger" sx={{ mt: 0.2 }}>
      {error}
    </Box>
  ) : null;
};
